<template>
  <div class="cookie-preferences">
    <!-- Necessary (always active) -->
    <div class="cookie-preferences__category">
      <div class="cookie-preferences__row">
        <div>
          <span class="cookie-preferences__name label">
            {{ t('CookieConsent.category.necessary.title') }}
          </span>
          <p v-if="showDescriptions" class="cookie-preferences__desc label-sm">
            {{ t('CookieConsent.category.necessary.description') }}
          </p>
        </div>
        <AtomBadge variant="success">
          {{ t('CookieConsent.category.necessary.badge') }}
        </AtomBadge>
      </div>
    </div>

    <!-- Toggleable categories -->
    <div
      v-for="cat in toggleableCategories"
      :key="cat.key"
      class="cookie-preferences__category">
      <div class="cookie-preferences__row">
        <div>
          <span class="cookie-preferences__name label">{{ cat.title }}</span>
          <p v-if="showDescriptions" class="cookie-preferences__desc label-sm">
            {{ cat.description }}
          </p>
        </div>
        <AtomSwitch
          :model-value="preferences[cat.key]"
          :is-normal="true"
          @update:model-value="preferences[cat.key] = $event" />
      </div>
    </div>

    <div class="cookie-preferences__actions">
      <AtomButton
        appearance="primary"
        :action-text="t('CookieConsent.settings.save')"
        @click="save" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { NotificationType } from '@/types/enums/notification.enum';

type PreferenceKey = 'functional' | 'statistical' | 'marketing';

const { t } = useI18n();
const notifyStore = useNotifyStore();
const { savePreferences, hasFunctional, hasStatistical, hasMarketing } =
  useCookieConsent();

defineProps<{
  showDescriptions?: boolean;
}>();

const emit = defineEmits<{
  saved: [];
}>();

const preferences = reactive<Record<PreferenceKey, boolean>>({
  functional: hasFunctional.value,
  statistical: hasStatistical.value,
  marketing: hasMarketing.value
});

const toggleableCategories = computed<
  { key: PreferenceKey; title: string; description: string }[]
>(() => [
  {
    key: 'functional',
    title: t('CookieConsent.category.functional.title'),
    description: t('CookieConsent.category.functional.description')
  },
  {
    key: 'statistical',
    title: t('CookieConsent.category.statistical.title'),
    description: t('CookieConsent.category.statistical.description')
  },
  {
    key: 'marketing',
    title: t('CookieConsent.category.marketing.title'),
    description: t('CookieConsent.category.marketing.description')
  }
]);

function showToast() {
  notifyStore.notify(
    t('CookieConsent.toast.heading'),
    t('CookieConsent.toast.message'),
    NotificationType.Success
  );
}

function save() {
  savePreferences(preferences);
  showToast();
  emit('saved');
}
</script>

<style scoped lang="scss">
.cookie-preferences__category {
  padding: 0.75rem 0;
  border-bottom: 1px solid $color-gray-200;

  &:last-of-type {
    border-bottom: none;
  }
}

.cookie-preferences__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.cookie-preferences__name {
  font-weight: font-weight('medium');
}

.cookie-preferences__desc {
  margin: 0.25rem 0 0;
  color: $color-gray-500;
}

.cookie-preferences__actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
}
</style>
